import React, { useState } from 'react';
import { Drawer, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { TaskDrawerProvider, useTaskDrawer } from './TaskDrawerContext';
import TaskActivityLog from './components/TaskActivityLog';
import TaskCollaborators from './components/TaskCollaborators';
import TaskReviewers from './components/TaskReviewers';
import TaskDescription from './components/TaskDescription';
import TaskFooter from './components/TaskFooter';
import TaskHeader from './components/TaskHeader';
import TaskMetadata from './components/TaskMetadata';
import TaskSubtasks from './components/TaskSubtasks';
import TaskTabs from './components/TaskTabs';
import { formatHHMMSS } from './utils/timeFormatters';

dayjs.extend(relativeTime);

function TaskDrawerShell() {
    const { open, onClose, closeAll, task, loading, loadError, loadTask, hasMask, drawerStack, itemType, mode } = useTaskDrawer();

    const isStacked = drawerStack && drawerStack.length > 1;
    const isMilestone = (task?.itemType === 'milestone') || (itemType === 'milestone');

    return (
        <Drawer
            open={open}
            onClose={closeAll || onClose}
            mask={hasMask !== false}
            width="min(900px, 96vw)"
            closable={false}
            className="relative font-body bg-white dark:bg-[#1f1f1f] text-[#1c1e21] dark:text-white"
        >
            <div className="h-1.5 w-full bg-gradient-to-r from-[#03488A] to-[#006591] shrink-0" />

            {loading && <TaskDrawerLoading />}
            {loadError && <TaskDrawerError onRetry={loadTask} />}

            {task && (
                <div className="flex flex-col h-full bg-white dark:bg-[#1f1f1f]">
                    {isStacked ? (
                        /* Stacked mode: two buttons — back (pop one level) + close all */
                        <div className="absolute -left-12 top-6 flex flex-col gap-2 z-10">
                            {/* Back to parent */}
                            <button
                                onClick={onClose}
                                className="w-10 h-10 bg-ed-surface-container-lowest rounded-full shadow-lg flex items-center justify-center hover:bg-primary hover:text-on-primary transition-all text-ed-on-surface border border-ed-surface-container-high"
                                title="Back to parent task"
                            >
                                <span className="material-symbols-outlined">arrow_back</span>
                            </button>
                            {/* Close entire drawer */}
                            <button
                                onClick={closeAll}
                                className="w-10 h-10 bg-ed-surface-container-lowest rounded-full shadow-lg flex items-center justify-center hover:bg-error hover:text-white transition-all text-ed-on-surface border border-ed-surface-container-high"
                                title="Close drawer"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                    ) : (
                        /* Normal mode: single close button */
                        <button
                            onClick={closeAll || onClose}
                            className="absolute -left-12 top-6 w-10 h-10 bg-ed-surface-container-lowest rounded-full shadow-lg flex items-center justify-center hover:bg-primary hover:text-on-primary transition-all text-ed-on-surface border border-ed-surface-container-high z-10"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    )}

                    <TaskHeader />
                    <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar flex">
                        <div className="flex-1 px-5 py-5 space-y-5 min-w-0 w-full">
                            <TaskMetadata />
                            {!isMilestone && <TaskCollaborators />}
                            {!isMilestone && <TaskReviewers />}
                            <TaskDescription />
                            <TaskSubtasks />
                            <TaskTabs />
                            <TaskActivityLog />
                        </div>
                    </div>
                    <TaskFooter />
                </div>
            )}
        </Drawer>
    );
}

function TaskDrawerLoading() {
    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-ed-surface-container-lowest/80 dark:bg-[#0f172a]/80 z-50">
            <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 36, color: '#03488A' }} spin />} />
            <span className="mt-4 text-xs font-semibold text-ed-on-surface-variant tracking-wider uppercase">Loading task details...</span>
        </div>
    );
}

function TaskDrawerError({ onRetry }) {
    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-ed-surface-container-lowest dark:bg-[#0f172a] z-50 text-center">
            <div className="w-16 h-16 rounded-full bg-error-container flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-error text-3xl">error</span>
            </div>
            <h3 className="text-lg font-bold text-ed-on-surface">Failed to load task</h3>
            <p className="text-sm text-ed-on-surface-variant mt-2 max-w-sm">
                There was an error retrieving the task details. Please check your connection and try again.
            </p>
            <button onClick={onRetry} className="mt-6 px-5 py-2.5 brand-gradient text-on-primary text-xs font-bold uppercase tracking-wider rounded-xl shadow-md hover:shadow-lg transition-all active:scale-95">
                Retry Fetch
            </button>
        </div>
    );
}

/**
 * TaskDetailDrawer
 * Manages a stack of task drawers for parent â†’ subtask navigation.
 * The bottom-most drawer has the backdrop mask; subsequent layers stack on top without masks.
 */
export default function TaskDetailDrawer(props) {
    const { taskId, open, onClose, mode = 'view' } = props || {};
    const [stack, setStack] = useState([]);

    // Sync stack when the drawer opens/closes or the root task changes
    React.useEffect(() => {
        if (open) {
            if (mode === 'create') {
                setStack([{ id: 'new', mode: 'create', title: 'New Task' }]);
            } else if (taskId) {
                setStack([{ id: taskId, mode, title: 'Task' }]);
            }
        } else {
            const timer = setTimeout(() => setStack([]), 350);
            return () => clearTimeout(timer);
        }
    }, [open, taskId, mode]);

    const pushTask = (newId, newMode = 'view', newTitle = 'Subtask', parentId = null) => {
        setStack((prev) => [...prev, { id: newId, mode: newMode, title: newTitle, parentId }]);
    };

    const updateStackItem = (id, title, itemType, parent = null) => {
        setStack((prev) => {
            // If drawer was opened directly on a child task, reconstruct the ancestor chain into the stack
            if (prev.length === 1 && prev[0].id === id && parent && typeof parent === 'object' && parent._id) {
                const ancestors = [];
                // Check if grandparent exists
                if (parent.parentId && typeof parent.parentId === 'object' && parent.parentId._id) {
                    ancestors.push({
                        id: parent.parentId._id,
                        mode: 'view',
                        title: parent.parentId.title || parent.parentId.taskId || 'Task',
                        itemType: parent.parentId.itemType || 'task',
                    });
                }
                // Add immediate parent
                ancestors.push({
                    id: parent._id,
                    mode: 'view',
                    title: parent.title || parent.taskId || 'Parent Task',
                    itemType: parent.itemType || 'task',
                });
                // Add current child task
                ancestors.push({
                    ...prev[0],
                    title: title || prev[0].title,
                    itemType: itemType || prev[0].itemType,
                });
                return ancestors;
            }
            return prev.map((item) => (item.id === id ? { ...item, title: title || item.title, itemType: itemType || item.itemType } : item));
        });
    };

    const popTask = () => {
        setStack((prev) => {
            if (prev.length <= 1) {
                onClose();
                return prev;
            }
            return prev.slice(0, -1);
        });
    };

    const popTo = (index) => {
        setStack((prev) => prev.slice(0, index + 1));
    };

    return (
        <>
            {stack.map((item, index) => {
                const isTop = index === stack.length - 1;
                // Only the bottom-most drawer shows the dark backdrop mask
                const hasMask = index === 0;

                return (
                    <TaskDrawerProvider
                        key={`${item.id}-${index}`}
                        value={{
                            ...props,
                            taskId: item.id,
                            mode: item.mode,
                            parentId: item.parentId,
                            open: true,
                            onClose: isTop ? popTask : undefined,
                            // closeAll always closes the entire drawer regardless of stack depth
                            closeAll: onClose,
                            pushTask,
                            popTask,
                            popTo,
                            updateStackItem,
                            drawerStack: stack.slice(0, index + 1),
                            isTop,
                            hasMask,
                        }}
                    >
                        <TaskDrawerShell />
                    </TaskDrawerProvider>
                );
            })}
        </>
    );
}

export { formatHHMMSS };

